import React, { useState, useEffect } from 'react';
import {
  Building,
  Phone,
  Mail,
  CreditCard,
  Banknote,
  FileText,
  AlertCircle,
  Check,
  Loader2,
  Trash2,
  Plus,
  Edit,
  Save,
} from 'lucide-react';
import { getCurrentBodyCorporateId, getCurrentUserId, supabase } from '../lib/supabase';
import AuditService from '../services/AuditService';
import toast from 'react-hot-toast';
import { readFileAsDataUrl } from '../lib/logoUpload';
import LevyService from '../services/LevyService';
import { getLocalDateString } from '../lib/dateUtils';
import { statementMergeFieldHelp } from '../lib/statementTemplate';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('body-corporate');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [bodyCorporateId, setBodyCorporateId] = useState<string | null>(null);

  // Body Corporate state
  const [bodyCorporate, setBodyCorporate] = useState({
    name: '',
    complex_name: '',
    physical_address: '',
    postal_address: '',
    telephone: '',
    email: '',
    registration_number: '',
    currency_symbol: 'R',
    currency_code: 'ZAR',
    logo_path: '',
    primary_color: '#111827',
    style_preset: 'classic',
  });

  // Bank Account state
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_name: '',
    account_number: '',
    branch_code: '',
    is_primary: false,
  });
  const [editingBankId, setEditingBankId] = useState<string | null>(null);
  const [showBankModal, setShowBankModal] = useState(false);

  // Statement Settings state
  const [statementSettings, setStatementSettings] = useState({
    template_name: 'Professional statement',
    layout_style: 'professional',
    statement_title: 'LEVY STATEMENT',
    intro_text: 'Dear {{owner_name}}, please find your levy statement for {{period_from}} to {{period_to}}. The amount currently due is {{amount_due}}.',
    summary_heading: 'Account summary',
    transactions_heading: 'Transaction history',
    payment_heading: 'Payment details',
    notice_text: 'Please ensure that payment is made by the due date. Please use your account number as the payment reference.',
    footer_text: '',
    payment_instructions: '',
    terms_text: '',
  });

  // Levy Rules state
  const [levyRules, setLevyRules] = useState<any[]>([]);
  const [levyForm, setLevyForm] = useState({
    name: '',
    levy_type: 'monthly',
    default_amount: '',
    due_day: 7,
    is_active: true,
    effective_from: getLocalDateString(),
    effective_to: '',
  });
  const [editingLevyId, setEditingLevyId] = useState<string | null>(null);
  const [showLevyModal, setShowLevyModal] = useState(false);

  // Penalty Rules state
  const [penaltyRules, setPenaltyRules] = useState<any[]>([]);
  const [penaltyForm, setPenaltyForm] = useState({
    name: '',
    penalty_type: 'percentage',
    amount_or_rate: '',
    grace_days: 0,
    apply_once: true,
    is_active: true,
    effective_from: getLocalDateString(),
    effective_to: '',
  });
  const [editingPenaltyId, setEditingPenaltyId] = useState<string | null>(null);
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      const id = await getCurrentBodyCorporateId();
      setBodyCorporateId(id);
      await fetchAllSettings(id);
    };

    load();
  }, []);

  const fetchAllSettings = async (id: string | null) => {
    setLoading(true);

    if (!id) {
      toast.error('No body corporate is configured for this user.');
      setLoading(false);
      return;
    }

    try {
      await Promise.all([
        fetchBodyCorporate(id),
        fetchBankAccounts(id),
        fetchStatementSettings(id),
        fetchLevyRules(id),
        fetchPenaltyRules(id),
      ]);
    } catch (error) {
      console.error(error);
      toast.error('Error loading settings');
    }
    setLoading(false);
  };

  const fetchBodyCorporate = async (id: string | null) => {
    if (!id) return;

    const { data, error } = await supabase
      .from('body_corporates')
      .select('*')
      .eq('id', id)
      .single();

    if (!error && data) {
      setBodyCorporate({
        ...data,
        // Treat the old application-blue default as uncustomised branding.
        primary_color: data.primary_color === '#2563eb' ? '#111827' : data.primary_color,
      });
    }
  };

  const fetchBankAccounts = async (id: string | null) => {
    if (!id) return;

    const { data, error } = await supabase
      .from('bank_accounts')
      .select('*')
      .eq('body_corporate_id', id)
      .order('is_primary', { ascending: false });

    if (!error && data) {
      setBankAccounts(data);
    }
  };

  const fetchStatementSettings = async (id: string | null) => {
    if (!id) return;

    const { data, error } = await supabase
      .from('statement_settings')
      .select('*')
      .eq('body_corporate_id', id)
      .single();

    if (!error && data) {
      setStatementSettings((current) => ({ ...current, ...data }));
    }
  };

  const applyStatementPreset = (preset: 'professional' | 'reminder') => {
    const shared = {
      layout_style: 'professional',
      summary_heading: 'Account summary',
      transactions_heading: 'Transaction history',
      payment_heading: 'Payment details',
    };
    setStatementSettings({
      ...statementSettings,
      ...shared,
      template_name: preset === 'professional' ? 'Professional statement' : 'Payment reminder',
      statement_title: preset === 'professional' ? 'LEVY STATEMENT' : 'LEVY PAYMENT REMINDER',
      intro_text: preset === 'professional'
        ? 'Dear {{owner_name}}, please find your levy statement for {{period_from}} to {{period_to}}. The amount currently due is {{amount_due}}.'
        : 'Dear {{owner_name}}, your account {{account_number}} has an amount due of {{amount_due}}. Please make payment by {{due_date}} using {{payment_reference}} as the reference.',
      notice_text: preset === 'professional'
        ? 'Please ensure that payment is made by the due date. Please use your account number as the payment reference.'
        : 'If payment has already been made, please disregard this reminder and send proof of payment to the managing agent.',
    });
  };

  const fetchLevyRules = async (id: string | null) => {
    if (!id) return;

    const { data, error } = await supabase
      .from('levy_rules')
      .select('*')
      .eq('body_corporate_id', id)
      .order('name');

    if (!error && data) {
      setLevyRules(data);
    }
  };

  const fetchPenaltyRules = async (id: string | null) => {
    if (!id) return;

    const { data, error } = await supabase
      .from('penalty_rules')
      .select('*')
      .eq('body_corporate_id', id)
      .order('name');

    if (!error && data) {
      setPenaltyRules(data);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setBodyCorporate({ ...bodyCorporate, logo_path: dataUrl });
      toast.success('Logo selected locally');
    } catch (error) {
      console.error(error);
      toast.error('Unable to read the selected logo file');
    }
  };

  // Save Body Corporate
  const handleSaveBodyCorporate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bodyCorporateId) {
      toast.error('REACT_APP_BODY_CORPORATE_ID is missing from your .env file');
      return;
    }

    setSaving(true);

    const { data: previousBodyCorporate } = await supabase
      .from('body_corporates')
      .select('*')
      .eq('id', bodyCorporateId)
      .single();

    const { error } = await supabase
      .from('body_corporates')
      .update(bodyCorporate)
      .eq('id', bodyCorporateId);

    if (error) {
      toast.error('Error saving settings');
      console.error(error);
    } else {
      const userId = await getCurrentUserId();
      if (userId) await new AuditService(bodyCorporateId, userId).logAction('UPDATE', 'body_corporate', bodyCorporateId, previousBodyCorporate, bodyCorporate);
      toast.success('Settings saved successfully');
    }
    setSaving(false);
  };

  // Save Statement Settings
  const handleSaveStatementSettings = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bodyCorporateId) {
      toast.error('REACT_APP_BODY_CORPORATE_ID is missing from your .env file');
      return;
    }

    setSaving(true);

    const { data: previousStatementSettings } = await supabase
      .from('statement_settings')
      .select('*')
      .eq('body_corporate_id', bodyCorporateId)
      .single();

    const { error } = await supabase
      .from('statement_settings')
      .update(statementSettings)
      .eq('body_corporate_id', bodyCorporateId);

    if (error) {
      toast.error('Error saving statement settings');
      console.error(error);
    } else {
      const userId = await getCurrentUserId();
      if (userId) await new AuditService(bodyCorporateId, userId).logAction('UPDATE', 'statement_settings', bodyCorporateId, previousStatementSettings, statementSettings);
      toast.success('Statement settings saved successfully');
    }
    setSaving(false);
  };

  // Bank Account CRUD
  const handleSaveBank = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    if (editingBankId) {
      const oldBank = bankAccounts.find((bank) => bank.id === editingBankId);
      const { error } = await supabase
        .from('bank_accounts')
        .update({
          ...bankForm,
          body_corporate_id: bodyCorporateId,
        })
        .eq('id', editingBankId);

      if (error) {
        toast.error('Error updating bank account');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('UPDATE', 'bank_account', editingBankId, oldBank, { ...oldBank, ...bankForm });
        toast.success('Bank account updated');
        fetchBankAccounts(bodyCorporateId);
        closeBankModal();
      }
    } else {
      const { data: createdBank, error } = await supabase
        .from('bank_accounts')
        .insert({
          ...bankForm,
          body_corporate_id: bodyCorporateId,
        })
        .select()
        .single();

      if (error) {
        toast.error('Error adding bank account');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId && createdBank) await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'bank_account', createdBank.id, undefined, createdBank);
        toast.success('Bank account added');
        fetchBankAccounts(bodyCorporateId);
        closeBankModal();
      }
    }
    setSaving(false);
  };

  const handleDeleteBank = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;

    const deletedBank = bankAccounts.find((bank) => bank.id === id);
    const { error } = await supabase
      .from('bank_accounts')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Error deleting bank account');
    } else {
      const userId = await getCurrentUserId();
      if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('DELETE', 'bank_account', id, deletedBank);
      toast.success('Bank account deleted');
      fetchBankAccounts(bodyCorporateId);
    }
  };

  const openBankModal = (bank?: any) => {
    if (bank) {
      setEditingBankId(bank.id);
      setBankForm({
        bank_name: bank.bank_name,
        account_name: bank.account_name,
        account_number: bank.account_number,
        branch_code: bank.branch_code || '',
        is_primary: bank.is_primary,
      });
    } else {
      setEditingBankId(null);
      setBankForm({
        bank_name: '',
        account_name: '',
        account_number: '',
        branch_code: '',
        is_primary: false,
      });
    }
    setShowBankModal(true);
  };

  const closeBankModal = () => {
    setShowBankModal(false);
    setEditingBankId(null);
  };

  // Levy Rules CRUD
  const handleSaveLevy = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...levyForm,
      body_corporate_id: bodyCorporateId,
      default_amount: parseFloat(levyForm.default_amount),
      effective_from: levyForm.effective_from,
      effective_to: levyForm.effective_to || null,
    };

    if (editingLevyId) {
      const oldLevy = levyRules.find((rule) => rule.id === editingLevyId);
      const { error } = await supabase
        .from('levy_rules')
        .update(data)
        .eq('id', editingLevyId);

      if (error) {
        toast.error('Error updating levy rule');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('UPDATE', 'levy_rule', editingLevyId, oldLevy, { ...oldLevy, ...data });
        toast.success('Levy rule updated');
        fetchLevyRules(bodyCorporateId);
        closeLevyModal();
      }
    } else {
      const { data: createdLevy, error } = await supabase
        .from('levy_rules')
        .insert(data)
        .select()
        .single();

      if (error) {
        toast.error('Error adding levy rule');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId && createdLevy) await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'levy_rule', createdLevy.id, undefined, createdLevy);
        toast.success('Levy rule added');
        fetchLevyRules(bodyCorporateId);
        closeLevyModal();
      }
    }
    setSaving(false);
  };

  const handleDeleteLevy = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this levy rule?')) return;

    const deletedLevy = levyRules.find((rule) => rule.id === id);
    const { error } = await supabase
      .from('levy_rules')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Error deleting levy rule');
    } else {
      const userId = await getCurrentUserId();
      if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('DELETE', 'levy_rule', id, deletedLevy);
      toast.success('Levy rule deleted');
      fetchLevyRules(bodyCorporateId);
    }
  };

  const openLevyModal = (rule?: any) => {
    if (rule) {
      setEditingLevyId(rule.id);
      setLevyForm({
        name: rule.name,
        levy_type: rule.levy_type,
        default_amount: rule.default_amount.toString(),
        due_day: rule.due_day,
        is_active: rule.is_active,
        effective_from: rule.effective_from || getLocalDateString(),
        effective_to: rule.effective_to || '',
      });
    } else {
      setEditingLevyId(null);
      setLevyForm({
        name: '',
        levy_type: 'monthly',
        default_amount: '',
        due_day: 7,
        is_active: true,
        effective_from: getLocalDateString(),
        effective_to: '',
      });
    }
    setShowLevyModal(true);
  };

  const closeLevyModal = () => {
    setShowLevyModal(false);
    setEditingLevyId(null);
  };

  // Penalty Rules CRUD
  const handleSavePenalty = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...penaltyForm,
      body_corporate_id: bodyCorporateId,
      amount_or_rate: parseFloat(penaltyForm.amount_or_rate),
      effective_from: penaltyForm.effective_from,
      effective_to: penaltyForm.effective_to || null,
      apply_once: true,
    };

    if (editingPenaltyId) {
      const oldPenalty = penaltyRules.find((rule) => rule.id === editingPenaltyId);
      const { error } = await supabase
        .from('penalty_rules')
        .update(data)
        .eq('id', editingPenaltyId);

      if (error) {
        toast.error('Error updating penalty rule');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('UPDATE', 'penalty_rule', editingPenaltyId, oldPenalty, { ...oldPenalty, ...data });
        toast.success('Penalty rule updated');
        fetchPenaltyRules(bodyCorporateId);
        closePenaltyModal();
      }
    } else {
      const { data: createdPenalty, error } = await supabase
        .from('penalty_rules')
        .insert(data)
        .select()
        .single();

      if (error) {
        toast.error('Error adding penalty rule');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId && createdPenalty) await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'penalty_rule', createdPenalty.id, undefined, createdPenalty);
        toast.success('Penalty rule added');
        fetchPenaltyRules(bodyCorporateId);
        closePenaltyModal();
      }
    }
    setSaving(false);
  };

  const handleDeletePenalty = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this penalty rule?')) return;

    const deletedPenalty = penaltyRules.find((rule) => rule.id === id);
    const { error } = await supabase
      .from('penalty_rules')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Error deleting penalty rule');
    } else {
      const userId = await getCurrentUserId();
      if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('DELETE', 'penalty_rule', id, deletedPenalty);
      toast.success('Penalty rule deleted');
      fetchPenaltyRules(bodyCorporateId);
    }
  };

  const openPenaltyModal = (rule?: any) => {
    if (rule) {
      setEditingPenaltyId(rule.id);
      setPenaltyForm({
        name: rule.name,
        penalty_type: rule.penalty_type,
        amount_or_rate: rule.amount_or_rate.toString(),
        grace_days: rule.grace_days,
        apply_once: true,
        is_active: rule.is_active,
        effective_from: rule.effective_from || getLocalDateString(),
        effective_to: rule.effective_to || '',
      });
    } else {
      setEditingPenaltyId(null);
      setPenaltyForm({
        name: '',
        penalty_type: 'percentage',
        amount_or_rate: '',
        grace_days: 0,
        apply_once: true,
        is_active: true,
        effective_from: getLocalDateString(),
        effective_to: '',
      });
    }
    setShowPenaltyModal(true);
  };

  const closePenaltyModal = () => {
    setShowPenaltyModal(false);
    setEditingPenaltyId(null);
  };

  const handleRunPenaltyCheck = async () => {
    if (!bodyCorporateId) {
      toast.error('No body corporate is configured for this penalty check.');
      return;
    }

    setSaving(true);
    try {
      const { penaltiesApplied } = await new LevyService(bodyCorporateId).applyLatePenalties();
      if (penaltiesApplied) {
        const userId = await getCurrentUserId();
        if (userId) await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'penalty_batch', getLocalDateString(), undefined, {
          penalties_applied: penaltiesApplied,
          as_of_date: getLocalDateString(),
        });
      }
      toast.success(penaltiesApplied
        ? `${penaltiesApplied} late-payment ${penaltiesApplied === 1 ? 'penalty was' : 'penalties were'} added.`
        : 'Penalty check complete. No new penalties were required.');
      await fetchPenaltyRules(bodyCorporateId);
    } catch (error) {
      console.error('Error running penalty check:', error);
      toast.error('Could not run the penalty check. Apply the financial-engine database migration first.');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateMonthlyLevies = async () => {
    if (!bodyCorporateId) {
      toast.error('No body corporate is configured for levy generation.');
      return;
    }

    const monthLabel = new Intl.DateTimeFormat('en-ZA', {
      month: 'long',
      year: 'numeric',
    }).format(new Date());

    if (!window.confirm(`Generate monthly levies for every active account for ${monthLabel}? Existing levies for this month will be skipped.`)) {
      return;
    }

    setSaving(true);
    try {
      const { created, skipped, total } = await new LevyService(bodyCorporateId).generateMonthlyLevies();
      toast.success(
        total === 0
          ? 'No active accounts were found to charge.'
          : `Monthly levies generated: ${created} created, ${skipped} already existed.`
      );
    } catch (error) {
      console.error('Error generating monthly levies:', error);
      if (LevyService.isAccountLevyRatePermissionError(error)) {
        toast.error('Database permission is missing for account levy rates. Run the supplied Supabase migration.');
      } else if (LevyService.isMissingLevySchemaError(error)) {
        toast.error('Could not generate monthly levies. Run the financial-engine SQL migration so levy rules and account levy rates exist.');
      } else {
        toast.error('Could not generate monthly levies. Check that an active monthly levy rule is configured.');
      }
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'body-corporate', label: 'Body Corporate', icon: Building },
    { id: 'banking', label: 'Banking', icon: Banknote },
    { id: 'levy-rules', label: 'Levy Rules', icon: CreditCard },
    { id: 'penalty-rules', label: 'Penalty Rules', icon: AlertCircle },
    { id: 'statements', label: 'Statements', icon: FileText },
  ];

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading settings...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Settings</h1>
      <p className="text-gray-500 mb-6">Configure your Body Corporate details and rules</p>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2.5 border-b-2 transition-colors whitespace-nowrap
              ${activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
              }
            `}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="max-w-3xl">
        {/* Body Corporate */}
        {activeTab === 'body-corporate' && (
          <form onSubmit={handleSaveBodyCorporate} className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Body Corporate Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Body Corporate Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={bodyCorporate.name}
                    onChange={(e) => setBodyCorporate({ ...bodyCorporate, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complex/Scheme Name
                  </label>
                  <input
                    type="text"
                    value={bodyCorporate.complex_name || ''}
                    onChange={(e) => setBodyCorporate({ ...bodyCorporate, complex_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Physical Address
                    </label>
                    <input
                      type="text"
                      value={bodyCorporate.physical_address || ''}
                      onChange={(e) => setBodyCorporate({ ...bodyCorporate, physical_address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Address
                    </label>
                    <input
                      type="text"
                      value={bodyCorporate.postal_address || ''}
                      onChange={(e) => setBodyCorporate({ ...bodyCorporate, postal_address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telephone
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        value={bodyCorporate.telephone || ''}
                        onChange={(e) => setBodyCorporate({ ...bodyCorporate, telephone: e.target.value })}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="email"
                        value={bodyCorporate.email || ''}
                        onChange={(e) => setBodyCorporate({ ...bodyCorporate, email: e.target.value })}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registration/Company Number
                  </label>
                  <input
                    type="text"
                    value={bodyCorporate.registration_number || ''}
                    onChange={(e) => setBodyCorporate({ ...bodyCorporate, registration_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logo
                    </label>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                    <div className="mt-2 text-xs text-gray-500">
                      Upload a logo from your computer. The image will be stored as a local data URL for the statement template.
                    </div>
                    {bodyCorporate.logo_path && (
                      <img src={bodyCorporate.logo_path} alt="Logo preview" className="w-20 h-20 rounded-xl object-cover mt-3 border border-gray-200" />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Colour
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={bodyCorporate.primary_color || '#111827'}
                        onChange={(e) => setBodyCorporate({ ...bodyCorporate, primary_color: e.target.value })}
                        className="w-12 h-10 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="text"
                        value={bodyCorporate.primary_color || '#111827'}
                        onChange={(e) => setBodyCorporate({ ...bodyCorporate, primary_color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">Professional statements use black ink on a white page.</p>
                      <button
                        type="button"
                        onClick={() => setBodyCorporate({ ...bodyCorporate, primary_color: '#111827', style_preset: 'minimal' })}
                        className="shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 hover:bg-slate-50"
                      >
                        Use black &amp; white
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Style Preset
                  </label>
                  <select
                    value={bodyCorporate.style_preset || 'classic'}
                    onChange={(e) => setBodyCorporate({ ...bodyCorporate, style_preset: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="classic">Classic</option>
                    <option value="modern">Modern</option>
                    <option value="minimal">Minimal</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency Symbol
                    </label>
                    <input
                      type="text"
                      value={bodyCorporate.currency_symbol}
                      onChange={(e) => setBodyCorporate({ ...bodyCorporate, currency_symbol: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency Code
                    </label>
                    <input
                      type="text"
                      value={bodyCorporate.currency_code}
                      onChange={(e) => setBodyCorporate({ ...bodyCorporate, currency_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center gap-2 py-2.5 px-6"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Save Changes
            </button>
          </form>
        )}

        {/* Banking */}
        {activeTab === 'banking' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Bank Accounts</h2>
              <button
                onClick={() => openBankModal()}
                className="btn-primary flex items-center gap-2 py-2 px-4"
              >
                <Plus size={18} />
                Add Bank Account
              </button>
            </div>

            {bankAccounts.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <Banknote className="mx-auto text-gray-400" size={48} />
                <p className="mt-2 text-gray-500">No bank accounts configured</p>
                <p className="text-sm text-gray-400">Add a bank account for payment details on statements</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bankAccounts.map((bank) => (
                  <div key={bank.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Banknote size={18} className="text-gray-400" />
                          <span className="font-medium">{bank.bank_name}</span>
                          {bank.is_primary && (
                            <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded-full">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          <p>Account: {bank.account_name}</p>
                          <p>Number: {bank.account_number}</p>
                          <p>Branch: {bank.branch_code || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openBankModal(bank)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteBank(bank.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Levy Rules */}
        {activeTab === 'levy-rules' && (
          <div>
            <div className="flex items-center justify-between mb-4 gap-2">
              <h2 className="text-lg font-semibold">Levy Rules</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleGenerateMonthlyLevies}
                  disabled={saving}
                  className="px-4 py-2 border border-primary-600 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-60 flex items-center gap-2"
                >
                  {saving ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                  {saving ? 'Generating…' : 'Generate Monthly Levies'}
                </button>
                <button
                  onClick={() => openLevyModal()}
                  className="btn-primary flex items-center gap-2 py-2 px-4"
                >
                  <Plus size={18} />
                  Add Levy Rule
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Creates this month&apos;s levy for every active account using its configured rate or the active monthly levy rule.
            </p>

            {levyRules.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <CreditCard className="mx-auto text-gray-400" size={48} />
                <p className="mt-2 text-gray-500">No levy rules configured</p>
                <p className="text-sm text-gray-400">Create levy rules for monthly, special, and other levies</p>
              </div>
            ) : (
              <div className="space-y-3">
                {levyRules.map((rule) => (
                  <div key={rule.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{rule.name}</span>
                          <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full capitalize">
                            {rule.levy_type}
                          </span>
                          {rule.is_active && (
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                              Active
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          <p>Amount: R {rule.default_amount.toFixed(2)}</p>
                          <p>Due Day: {rule.due_day}th of the month</p>
                          <p>Active from: {rule.effective_from || 'Immediately'}{rule.effective_to ? ` to ${rule.effective_to}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openLevyModal(rule)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteLevy(rule.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Penalty Rules */}
        {activeTab === 'penalty-rules' && (
          <div>
            <div className="flex items-center justify-between mb-4 gap-2">
              <h2 className="text-lg font-semibold">Penalty Rules</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleRunPenaltyCheck}
                  disabled={saving}
                  className="px-4 py-2 border border-primary-600 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-60"
                >
                  {saving ? 'Checking…' : 'Run Penalty Check'}
                </button>
                <button
                  onClick={() => openPenaltyModal()}
                  className="btn-primary flex items-center gap-2 py-2 px-4"
                >
                  <Plus size={18} />
                  Add Penalty Rule
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> A penalty applies once to the outstanding balance of a levy after its due date (the 7th by default) and any grace days. The whole due date is penalty-free. Use this check as an administrator backup to the scheduled daily check.
              </p>
            </div>

            {penaltyRules.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <AlertCircle className="mx-auto text-gray-400" size={48} />
                <p className="mt-2 text-gray-500">No penalty rules configured</p>
                <p className="text-sm text-gray-400">Configure penalty rules for late payments</p>
              </div>
            ) : (
              <div className="space-y-3">
                {penaltyRules.map((rule) => (
                  <div key={rule.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{rule.name}</span>
                          <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full capitalize">
                            {rule.penalty_type}
                          </span>
                          {rule.is_active && (
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                              Active
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          <p>
                            {rule.penalty_type === 'percentage' 
                              ? `${rule.amount_or_rate}% of amount`
                              : `R ${rule.amount_or_rate.toFixed(2)}`}
                          </p>
                          <p>Grace Days: {rule.grace_days}</p>
                          <p>Applied once per overdue levy</p>
                          <p>Active from: {rule.effective_from || 'Immediately'}{rule.effective_to ? ` to ${rule.effective_to}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openPenaltyModal(rule)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeletePenalty(rule.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Statements */}
        {activeTab === 'statements' && (
          <form onSubmit={handleSaveStatementSettings} className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-5">
                <div>
                  <h2 className="text-lg font-semibold">Statement template</h2>
                  <p className="mt-1 text-sm text-gray-500">Create and save the wording used on every statement. Your template is applied to both the PDF and preview.</p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => applyStatementPreset('professional')} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50">Professional preset</button>
                  <button type="button" onClick={() => applyStatementPreset('reminder')} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50">Reminder preset</button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                    <input type="text" value={statementSettings.template_name || ''} onChange={(e) => setStatementSettings({ ...statementSettings, template_name: e.target.value })} placeholder="e.g. September levy notice" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Layout</label>
                    <select value={statementSettings.layout_style || 'professional'} onChange={(e) => setStatementSettings({ ...statementSettings, layout_style: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                      <option value="professional">Professional — spacious and formal</option>
                      <option value="compact">Compact — shorter statements</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statement Title
                  </label>
                  <input
                    type="text"
                    value={statementSettings.statement_title}
                    onChange={(e) => setStatementSettings({ ...statementSettings, statement_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Introduction</label>
                  <textarea value={statementSettings.intro_text || ''} onChange={(e) => setStatementSettings({ ...statementSettings, intro_text: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="A personal message shown before the account summary" />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Summary Heading</label>
                    <input type="text" value={statementSettings.summary_heading || ''} onChange={(e) => setStatementSettings({ ...statementSettings, summary_heading: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transactions Heading</label>
                    <input type="text" value={statementSettings.transactions_heading || ''} onChange={(e) => setStatementSettings({ ...statementSettings, transactions_heading: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Heading</label>
                    <input type="text" value={statementSettings.payment_heading || ''} onChange={(e) => setStatementSettings({ ...statementSettings, payment_heading: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                </div>
                <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-800"><strong>Merge fields:</strong> {statementMergeFieldHelp}</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notice Text
                  </label>
                  <textarea
                    value={statementSettings.notice_text || ''}
                    onChange={(e) => setStatementSettings({ ...statementSettings, notice_text: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter the notice that appears at the bottom of statements"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Footer Text
                  </label>
                  <input
                    type="text"
                    value={statementSettings.footer_text || ''}
                    onChange={(e) => setStatementSettings({ ...statementSettings, footer_text: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Instructions
                  </label>
                  <textarea
                    value={statementSettings.payment_instructions || ''}
                    onChange={(e) => setStatementSettings({ ...statementSettings, payment_instructions: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Additional payment instructions"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Terms & Conditions
                  </label>
                  <textarea
                    value={statementSettings.terms_text || ''}
                    onChange={(e) => setStatementSettings({ ...statementSettings, terms_text: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Terms and conditions"
                  />
                </div>
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center gap-2 py-2.5 px-6"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Save Changes
            </button>
          </form>
        )}
      </div>

      {/* Bank Modal */}
      {showBankModal && (
        <Modal
          title={editingBankId ? 'Edit Bank Account' : 'Add Bank Account'}
          onClose={closeBankModal}
          onSubmit={handleSaveBank}
          saving={saving}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name *
              </label>
              <input
                type="text"
                required
                value={bankForm.bank_name}
                onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Name *
              </label>
              <input
                type="text"
                required
                value={bankForm.account_name}
                onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number *
              </label>
              <input
                type="text"
                required
                value={bankForm.account_number}
                onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch Code
              </label>
              <input
                type="text"
                value={bankForm.branch_code}
                onChange={(e) => setBankForm({ ...bankForm, branch_code: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bankForm.is_primary}
                onChange={(e) => setBankForm({ ...bankForm, is_primary: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="text-sm text-gray-700">Set as primary bank account</span>
            </label>
          </div>
        </Modal>
      )}

      {/* Levy Modal */}
      {showLevyModal && (
        <Modal
          title={editingLevyId ? 'Edit Levy Rule' : 'Add Levy Rule'}
          onClose={closeLevyModal}
          onSubmit={handleSaveLevy}
          saving={saving}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rule Name *
              </label>
              <input
                type="text"
                required
                value={levyForm.name}
                onChange={(e) => setLevyForm({ ...levyForm, name: e.target.value })}
                placeholder="Monthly Levy 2026"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Levy Type
              </label>
              <select
                value={levyForm.levy_type}
                onChange={(e) => setLevyForm({ ...levyForm, levy_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="special">Special</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  value={levyForm.default_amount}
                  onChange={(e) => setLevyForm({ ...levyForm, default_amount: e.target.value })}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Day (Day of Month)
              </label>
              <input
                type="number"
                min="1"
                max="31"
                value={levyForm.due_day}
                onChange={(e) => setLevyForm({ ...levyForm, due_day: parseInt(e.target.value) || 7 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Default: 7th of every month</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effective From *</label>
                <input
                  type="date"
                  required
                  value={levyForm.effective_from}
                  onChange={(e) => setLevyForm({ ...levyForm, effective_from: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effective To</label>
                <input
                  type="date"
                  min={levyForm.effective_from}
                  value={levyForm.effective_to}
                  onChange={(e) => setLevyForm({ ...levyForm, effective_to: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={levyForm.is_active}
                onChange={(e) => setLevyForm({ ...levyForm, is_active: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>
        </Modal>
      )}

      {/* Penalty Modal */}
      {showPenaltyModal && (
        <Modal
          title={editingPenaltyId ? 'Edit Penalty Rule' : 'Add Penalty Rule'}
          onClose={closePenaltyModal}
          onSubmit={handleSavePenalty}
          saving={saving}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rule Name *
              </label>
              <input
                type="text"
                required
                value={penaltyForm.name}
                onChange={(e) => setPenaltyForm({ ...penaltyForm, name: e.target.value })}
                placeholder="Late Payment Penalty"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Penalty Type
              </label>
              <select
                value={penaltyForm.penalty_type}
                onChange={(e) => setPenaltyForm({ ...penaltyForm, penalty_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="percentage">Percentage of Amount</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {penaltyForm.penalty_type === 'percentage' ? 'Percentage Rate (%)' : 'Fixed Amount (R)'} *
              </label>
              <input
                type="number"
                required
                step={penaltyForm.penalty_type === 'percentage' ? '0.01' : '0.01'}
                min="0"
                value={penaltyForm.amount_or_rate}
                onChange={(e) => setPenaltyForm({ ...penaltyForm, amount_or_rate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grace Days
              </label>
              <input
                type="number"
                min="0"
                value={penaltyForm.grace_days}
                onChange={(e) => setPenaltyForm({ ...penaltyForm, grace_days: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Number of days after due date before penalty applies</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effective From *</label>
                <input
                  type="date"
                  required
                  value={penaltyForm.effective_from}
                  onChange={(e) => setPenaltyForm({ ...penaltyForm, effective_from: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effective To</label>
                <input
                  type="date"
                  min={penaltyForm.effective_from}
                  value={penaltyForm.effective_to}
                  onChange={(e) => setPenaltyForm({ ...penaltyForm, effective_to: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">Penalties are applied once per levy. Fixed and percentage rules are supported.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={penaltyForm.is_active}
                onChange={(e) => setPenaltyForm({ ...penaltyForm, is_active: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Modal Component
interface ModalProps {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  saving?: boolean;
}

const Modal: React.FC<ModalProps> = ({ title, children, onClose, onSubmit, saving }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          {children}
          <div className="flex gap-3 mt-6">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 btn-primary flex items-center justify-center gap-2">
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add X icon if not imported
const X = (props: any) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default Settings;
